import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { client } from "@/lib/sanity";

import Table from "@/components/elements/Table/Table";
import TitleBar from "@/components/elements/TitleBar/TitleBar";
import ImageWithContentLeft from "@/components/elements/ImageWithContent/ImageWithContentLeft";
import TitleWithContent from "@/components/elements/TitleWithContent/TitleWithContent";
import CustomLists from "@/components/elements/CustomLists/CustomLists";
import { PortableText } from "@portabletext/react";


const fadeInUp = {
  initial: { opacity: 0, y: 50 },
  whileInView: { opacity: 1, y: 0 },
  transition: { duration: 0.8, ease: "easeOut" },
  viewport: { once: false, amount: 0.2 },
};

const portableComponents = {
  block: {
    normal: ({ children }: any) => (
      <p
        className="table-text-size text-justify tracking-tight break-normal word-spacing-wide leading-relaxed"
        style={{ all: 'unset', display: 'block' }} // reset inherited styles, keep block display
      >
        {children}
      </p>
    ),
  },
};

const splitIntoColumns = (arr: string[], columns = 2): string[][] => {
  const result: string[][] = Array.from({ length: columns }, () => []);
  arr.forEach((item, index) => {
    result[index % columns].push(item);
  });
  return result;
};

const SanityProductDetailPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const [product, setProduct] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!slug) return;

    const fetchProduct = async () => {
      const query = `*[_type == "product" && slug.current == $slug][0]{
        title,
        "imageUrl": thumbnail.asset->url,
        description,
        advantages,
        uses,
        propertyTable,
        "category": category->title,
        packing,
        advantagesNote,
        usesNote,
      }`;
      try {
        const result = await client.fetch(query, { slug });
        setProduct(result);
      } catch (err) {
        console.error("Sanity fetch error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [slug]);

  if (loading) return <p className="p-10 text-center">Loading...</p>;
  if (!product) return <p className="p-10 text-center">Product not found</p>;

  return (
    <div className="pb-0 px-4 2xl:px-40 xl:px-40 md:px-[3rem] my-[5rem]">
      <TitleBar title={product.title} />

      {product.imageUrl && (
        <motion.div {...fadeInUp}>
          <ImageWithContentLeft
            imageUrl={product.imageUrl}
            title="Description"
            content={product.description}
          />
        </motion.div>
      )}

    {product.advantages?.length > 0 && (
  <motion.div {...fadeInUp}>
    <TitleWithContent title={`Advantages`}>
      {product.advantagesNote && (
        <PortableText value={product.advantagesNote} components={portableComponents} />
      )}
      <CustomLists layout="split" items={splitIntoColumns(product.advantages, 2)} />
    </TitleWithContent>
  </motion.div>
)}

{product.uses?.length > 0 && (
  <motion.div {...fadeInUp}>
    <TitleWithContent title="Uses">
      {product.usesNote && (
        <PortableText value={product.usesNote} components={portableComponents} />
      )}
      <CustomLists layout="split" items={splitIntoColumns(product.uses, 2)} />
    </TitleWithContent>
  </motion.div>
)}

      {product.propertyTable?.length > 0 && (
      <motion.div {...fadeInUp}>
        <TitleWithContent title="Properties">
          <div className="table-wrapper max-w-5xl mx-auto overflow-x-auto ">
          <Table
            items={{
              rows: product.propertyTable.map((row: any) => [row.property, row.value])
            }}
          />
          </div>
        </TitleWithContent>
      </motion.div>
     )}

      {product.packing && (
        <motion.div {...fadeInUp}>
          <TitleWithContent title="Packing">
            <PortableText value={product.packing} components={portableComponents}/>
          </TitleWithContent>
        </motion.div>
      )}

    </div>
  );
};

export default SanityProductDetailPage;
